/**
 * Statuts de projet vus par l'interface.
 *
 * La liste et les libellés sont ceux du serveur, dans `backend/src/config/project-status.ts`.
 * En revanche l'interface ne décide jamais si un projet est clos : l'API renvoie `closed`,
 * calculé là où la règle vit. `isClosedProjectStatus` ne sert qu'aux quelques endroits qui
 * n'ont qu'un statut sous la main, sans l'objet projet complet.
 */

export const PROJECT_STATUSES = [
    'A démarrer',
    'En cours',
    'Pris en compte',
    'Gelé',
    'A Facturer',
    'Terminé',
    'Clôturé',
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** Statuts anglais d'avant la migration 005, encore présents en base. */
const LEGACY_LABELS: Record<string, string> = {
    draft: 'A démarrer',
    active: 'En cours',
    on_hold: 'Gelé',
    completed: 'Terminé',
    cancelled: 'Annulé',
};

const CLOSED = ['Clôturé', 'Terminé', 'completed', 'cancelled'];

/** Vrai si ce statut ferme la saisie. Le serveur reste l'autorité. */
export function isClosedProjectStatus(status?: string | null): boolean {
    return CLOSED.includes(String(status || ''));
}

/** Libellé lisible, y compris pour un statut anglais hérité. */
export function projectStatusLabel(status?: string | null): string {
    const value = String(status || '');
    return LEGACY_LABELS[value] || value || '—';
}

/**
 * Mise en forme commune des pastilles. La feuille de style ne porte pas de classe
 * `.badge` : les classes `badge-draft` et compagnie décrivent chacune leur propre
 * gabarit, mais `badge-default`, `badge-warning` et `badge-purple` ne portent que des
 * couleurs. Le gabarit est donc donné ici, comme le fait déjà la pastille de rôle.
 */
const BADGE_SHAPE = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

/**
 * Classe de pastille, gabarit compris. Les couleurs suivent le sens : en cours au vert,
 * à facturer en attente d'action, clôturé en gris éteint.
 */
export function projectStatusBadgeClass(status?: string | null): string {
    return `${BADGE_SHAPE} ${statusColorClass(status)}`;
}

function statusColorClass(status?: string | null): string {
    switch (String(status || '')) {
        case 'En cours':
        case 'active':
            return 'badge-approved';
        case 'A Facturer':
            return 'badge-warning';
        case 'A démarrer':
        case 'draft':
        case 'Pris en compte':
            return 'badge-submitted';
        case 'Gelé':
        case 'on_hold':
            return 'badge-purple';
        case 'Clôturé':
        case 'Terminé':
        case 'completed':
            return 'badge-default';
        case 'cancelled':
            return 'badge-rejected';
        default:
            return 'badge-default';
    }
}

/**
 * Statuts proposés au changement manuel. « Terminé » vient de SharePoint et reste affiché
 * s'il est déjà posé, mais on ne le propose pas : dans le CRA, la fin d'un projet se dit
 * « Clôturé », et deux mots pour la même chose sèmeraient le doute.
 */
export const PROJECT_STATUS_CHOICES: readonly ProjectStatus[] = [
    'A démarrer',
    'En cours',
    'Pris en compte',
    'Gelé',
    'A Facturer',
    'Clôturé',
];
